import { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";

import React, { useState, useMemo, useEffect } from "react";
import {
  ToggleButton,
  ToggleButtonGroup,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@mui/material";

import GaneshDataGridServerProcessing from "../DataGrid/GaneshDataGridServerProcessing";
import {
  flattenAnalysisRows,
  warningKeys,
  problemKeys,
} from "../../utils/flattenAnalysisRows";
import {
  legacyHiddenProblemColumns,
  legacyHiddenWarningColumns,
  makeIssueColumn,
} from "../../utils/analysisGrid";
import { Box } from "@mui/material";
import {
  getDateValue,
  renderHumanReadableDateCell,
  renderNumberCell,
  serverFilterDateOperators,
} from "utils/dataGrid";
import { DatasetType } from "../../types/Compliance";

type AnalysisCountsGridProps = {
  datasetType: DatasetType;
};

const defaultGridState: GridInitialState = {
  filter: { filterModel: { items: [] } },
  sorting: { sortModel: [{ field: "analysis_report_date", sort: "desc" }] },
  pagination: { paginationModel: { pageSize: 50, page: 0 } },
  pinnedColumns: { left: [] },
  density: "compact",
};

const getBaseColumns = (datasetType: DatasetType): GridColDef[] => {
  const base: GridColDef[] = [
    {
      field: "dataset_id",
      headerName: "Dataset ID",
      width: 75,
      type: "number",
    },
    { field: "dataset_name", headerName: "Dataset Name", width: 150 },
    {
      field: "country_code",
      headerName: "Country",
      sortable: false,
      filterable: false,
      width: 85,
    },
  ];

  if (!["country", "state"].includes(datasetType)) {
    base.push(
      { field: "customer_id", headerName: "Customer ID", width: 90 },
      { field: "customer_name", headerName: "Customer Name", width: 150 }
    );
  }

  base.push(
    {
      field: "analysis_report_date",
      headerName: "Report Date",
      width: 120,
      type: "date",
      valueGetter: getDateValue,
      renderCell: renderHumanReadableDateCell,
      filterOperators: serverFilterDateOperators,
    },
    {
      field: "total_volume",
      headerName: "Total Volume",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
    }
  );

  return base;
};

// Generates columns for issues (problems and warnings) based on keys and value type
// Unnest the problem and warning keys to create columns for each issue type
export const getNestedColumns = (
  problemKeys: string[],
  warningKeys: string[],
  valueType: "total" | "unreviewed" | "reviewed",
  asPercentage: boolean
): GridColDef[] => {
  const visibilityModel = getColumnVisibilityModel(valueType);

  return [
    ...problemKeys.map((key) => {
      const field = `problems_${key}_${valueType}`;
      return {
        ...makeIssueColumn("problems", key, valueType, asPercentage),
        field,
        hide: visibilityModel[field] === false,
      };
    }),
    ...warningKeys.map((key) => {
      const field = `warnings_${key}_${valueType}`;
      return {
        ...makeIssueColumn("warnings", key, valueType, asPercentage),
        field,
        hide: visibilityModel[field] === false,
      };
    }),
  ];
};

// Generates the column visibility model based on the value type
export function getColumnVisibilityModel(
  valueType: "total" | "reviewed" | "unreviewed" = "total"
): Record<string, boolean> {
  const hiddenFields = [
    ...legacyHiddenProblemColumns.map((k) => `problems_${k}_${valueType}`),
    ...legacyHiddenWarningColumns.map((k) => `warnings_${k}_${valueType}`),
  ];
  return hiddenFields.reduce((acc, key) => ({ ...acc, [key]: false }), {});
}

const AnalysisCountsGrid: React.FC<AnalysisCountsGridProps> = ({
  datasetType,
}) => {
  const fetchApi = "/v1/analyses/analysis/details";
  const fetchParams = {
    dataset_type: datasetType,
  };
  const [valueType, setValueType] = useState<
    "total" | "unreviewed" | "reviewed"
  >("total");
  const [asPercentage, setAsPercentage] = useState(false);

  const nestedColumns = useMemo(
    () =>
      getNestedColumns(
        [...problemKeys],
        [...warningKeys],
        valueType,
        asPercentage
      ),
    [valueType, asPercentage]
  );

  const baseColumns = useMemo(() => getBaseColumns(datasetType), [datasetType]);

  const initialSortingModel = useMemo(
    () => [{ field: "analysis_report_date", sort: "desc" }],
    []
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() =>
    getColumnVisibilityModel(valueType)
  );

  useEffect(() => {
    setColumnVisibilityModel(getColumnVisibilityModel(valueType));
  }, [valueType]);

  const initialState = useMemo(
    () => ({
      ...defaultGridState,
      columns: {
        ...defaultGridState.columns,
        columnVisibilityModel: getColumnVisibilityModel(valueType),
      },
    }),
    [valueType, datasetType]
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          value={valueType}
          exclusive
          onChange={(e, newValue) => newValue && setValueType(newValue)}
          // sx={{ mb: 2 }}
        >
          <ToggleButton value="total">Total</ToggleButton>
          <ToggleButton value="unreviewed">Unreviewed</ToggleButton>
          <ToggleButton value="reviewed">Reviewed</ToggleButton>
        </ToggleButtonGroup>

        <FormGroup row>
          <FormControlLabel
            control={
              <Switch
                checked={asPercentage}
                onChange={(e) => setAsPercentage(e.target.checked)}
              />
            }
            label="Show as percentage"
          />
        </FormGroup>
      </Box>

      <GaneshDataGridServerProcessing
        key={datasetType}
        gridId={`${datasetType}AnalysisGrid`}
        fetchApi={fetchApi}
        fetchParams={fetchParams}
        columns={[...baseColumns, ...nestedColumns]}
        initialState={initialState}
        initialSortingModel={initialSortingModel}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        rowTransformer={flattenAnalysisRows}
        getRowId={(row: any) => row.dataset_id}
        customHeight="750px"
      />
    </Box>
  );
};

export default AnalysisCountsGrid;
